import { forwardRef } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Box, { BoxProps } from '@mui/material/Box';
// routes
import { RouterLink } from 'src/routes/components';

// ----------------------------------------------------------------------

export interface LogoProps extends BoxProps {
  disabledLink?: boolean;
}

const Logo = forwardRef<HTMLDivElement, LogoProps>(
  ({ disabledLink = false, sx, ...other }, ref) => {
    const theme = useTheme();

    const PRIMARY_LIGHT = theme.palette.primary.light;

    const PRIMARY_MAIN = theme.palette.primary.main;

    const PRIMARY_DARK = theme.palette.primary.dark;

    // OR using local (public folder)
    // -------------------------------------------------------
    // const logo = (
    //   <Box
    //     component="img"
    //     src="/logo/logo_single.svg"
    //     sx={{ width: 40, height: 40, cursor: 'pointer', ...sx }}
    //   />
    // );

    const logo = (
      <Box
        ref={ref}
        component="div"
        sx={{
          width: 40,
          height: 40,
          ...sx,
        }}
        {...other}
      >
        <svg
          width="196"
          height="90"
          viewBox="0 0 196 105"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_9447_8574)">
            <path
              d="M46.1778 58.394L12.1269 78.0548C7.32596 80.8279 1.33252 77.3657 1.33252 71.8251V32.5035C1.33252 26.9629 7.32736 23.5007 12.1269 26.271L19.8767 30.7457H19.881L42.0139 43.5271L46.1778 45.9318C50.9788 48.6993 50.9788 55.6279 46.1778 58.3954V58.394Z"
              fill="#FEFEFE"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M11.2245 77.4296L48.0937 56.3436C51.5143 54.1661 50.0251 49.7535 47.1392 46.1387C43.8266 50.0365 38.4567 50.9566 35.1283 49.0853C34.5479 48.8321 33.8729 48.9764 33.3203 49.29C32.7696 49.6045 32.3394 50.2771 32.2084 50.9399C30.5775 58.6739 23.2873 63.2273 16.1659 60.8872C16.0798 60.8589 15.9928 60.8367 15.9055 60.8205C15.212 61.169 14.4981 61.5097 13.7629 61.8415C13.5461 62.2039 13.4226 62.6299 13.4167 63.0726C13.2859 69.8611 7.69149 73.8892 2.21111 74.0797C1.7521 74.0958 1.32751 74.2704 0.984375 74.5581C3.00709 78.2044 7.46413 79.8282 11.2245 77.4296Z"
              fill="#EBEBFD"
            />
            <path
              d="M17.2969 64.4297C17.2969 65.0122 16.8247 65.4844 16.2422 65.4844C15.6597 65.4844 15.1875 65.0122 15.1875 64.4297C15.1875 63.8472 15.6597 63.375 16.2422 63.375C16.8247 63.375 17.2969 63.8472 17.2969 64.4297Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M30.7969 61.336C30.7969 61.9184 30.3247 62.3906 29.7422 62.3906C29.1597 62.3906 28.6875 61.9184 28.6875 61.336C28.6875 60.7535 29.1597 60.2813 29.7422 60.2813C30.3247 60.2813 30.7969 60.7535 30.7969 61.336Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M24.6094 65.4141C24.6094 65.9965 24.1372 66.4688 23.5547 66.4688C22.9722 66.4688 22.5 65.9965 22.5 65.4141C22.5 64.8316 22.9722 64.3594 23.5547 64.3594C24.1372 64.3594 24.6094 64.8316 24.6094 65.4141Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M41.2031 52.0547C41.2031 52.6372 40.731 53.1094 40.1485 53.1094C39.566 53.1094 39.0938 52.6372 39.0938 52.0547C39.0938 51.4722 39.566 51 40.1485 51C40.731 51 41.2031 51.4722 41.2031 52.0547Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M43.875 54.7266C43.875 55.309 43.4028 55.7812 42.8203 55.7812C42.2378 55.7812 41.7656 55.309 41.7656 54.7266C41.7656 54.1441 42.2378 53.6719 42.8203 53.6719C43.4028 53.6719 43.875 54.1441 43.875 54.7266Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.9062 71.3203C14.9062 71.9028 14.4341 72.375 13.8516 72.375C13.2691 72.375 12.7969 71.9028 12.7969 71.3203C12.7969 70.7378 13.2691 70.2657 13.8516 70.2657C14.4341 70.2657 14.9062 70.7378 14.9062 71.3203Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9.84375 74.836C9.84375 75.4184 9.37158 75.8906 8.78908 75.8906C8.20658 75.8906 7.73438 75.4184 7.73438 74.836C7.73438 74.2535 8.20658 73.7813 8.78908 73.7813C9.37158 73.7813 9.84375 74.2535 9.84375 74.836Z"
              stroke="#C6C5F4"
              stroke-width="0.49339"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.7656 68.5782L15.178 69.4028"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M32.7656 61.5469L33.178 62.3716"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M20.1094 63.5157L20.5217 64.3403"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M36.9844 59.8594L37.3967 60.6841"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M25.843 62.644L26.6004 62.1183"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M39.0938 55.3594L39.5061 56.1841"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M36.7031 51.8438L37.1155 52.6685"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M43.5116 51.1593L44.3696 50.8217"
              stroke="#C6C5F4"
              stroke-width="0.771551"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10.5469 72.3652L11.3715 71.9529"
              stroke="#C6C5F4"
              stroke-width="0.771518"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19.5469 67.8652V67.4529"
              stroke="#C6C5F4"
              stroke-width="0.771525"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M11.6016 49.9058C13.0384 49.9058 14.2031 48.741 14.2031 47.3042C14.2031 45.8674 13.0384 44.7026 11.6016 44.7026C10.1648 44.7026 9 45.8674 9 47.3042C9 48.741 10.1648 49.9058 11.6016 49.9058Z"
              fill="#FEFEFE"
            />
            <path
              d="M8.78906 60.5933C10.6919 60.5933 12.2344 59.0507 12.2344 57.1479C12.2344 55.2452 10.6919 53.7026 8.78906 53.7026C6.88627 53.7026 5.34375 55.2452 5.34375 57.1479C5.34375 59.0507 6.88627 60.5933 8.78906 60.5933Z"
              fill="#FFA4AC"
            />
            <path
              opacity="0.19"
              d="M33.2578 60.5933C35.1606 60.5933 36.7031 59.0507 36.7031 57.1479C36.7031 55.2452 35.1606 53.7026 33.2578 53.7026C31.355 53.7026 29.8125 55.2452 29.8125 57.1479C29.8125 59.0507 31.355 60.5933 33.2578 60.5933Z"
              fill="#FFA4AC"
            />
            <path
              d="M8.54297 80.4411C7.07625 80.4411 5.60953 80.0558 4.27219 79.2837C1.5975 77.7397 0 74.9722 0 71.8826V32.5611C0 29.4715 1.59609 26.704 4.27078 25.16C6.94547 23.6145 10.1419 23.6131 12.8194 25.1572L46.8703 44.8179C49.5478 46.362 51.1453 49.1281 51.1453 52.219C51.1453 55.31 49.5464 58.0761 46.8689 59.6201L12.8208 79.2809C11.482 80.0544 10.0125 80.4411 8.54297 80.4411ZM8.51906 26.704C7.43484 26.704 6.35063 27.012 5.37891 27.6279C3.67031 28.7108 2.67188 30.6289 2.67188 32.6525V71.8826C2.67188 73.9962 3.76453 75.889 5.59406 76.9451C7.42359 78.0012 9.61031 78.0012 11.4413 76.9423L45.4922 57.2815C47.3231 56.2254 48.4172 54.3326 48.4172 52.219C48.4172 50.1054 47.3231 48.2126 45.4922 47.1565L19.5075 32.1519L19.1897 31.9704L11.4413 27.4944C10.5258 26.967 9.52172 26.7026 8.51766 26.7026L8.51906 26.704Z"
              fill="#6F3B66"
            />
            <path
              d="M27.198 55.873C26.6426 58.7923 24.0776 60.9988 20.9979 60.9988C19.3315 60.9988 17.8156 60.3547 16.6891 59.3C15.7301 58.4056 15.0495 57.2131 14.7977 55.873C14.6796 55.2598 15.1437 54.6875 15.7681 54.6875H26.2277C26.4302 54.6875 26.6173 54.748 26.772 54.852C27.0926 55.0658 27.2768 55.4609 27.1995 55.8744L27.198 55.873Z"
              fill="#F48B7C"
            />
            <path
              d="M27.1984 55.8939C26.6429 58.8133 24.0779 61.0197 20.9982 61.0197C19.3318 61.0197 17.8159 60.3756 16.6895 59.321C22.3327 57.7024 25.2676 56.0444 26.7709 54.8716C27.0915 55.0853 27.2757 55.4805 27.1984 55.8939Z"
              fill="#EF5944"
            />
            <path
              d="M20.925 61.5691C17.7131 61.5691 14.9442 59.2924 14.3255 56.1495C14.2608 55.8246 14.2861 55.4829 14.4225 55.1805C14.6826 54.6082 15.2339 54.2637 15.8315 54.2637H25.9748C26.3433 54.2637 26.7103 54.3762 26.9944 54.6124C27.4401 54.9837 27.637 55.5518 27.5315 56.1045C26.9297 59.2699 24.1509 61.5677 20.9236 61.5677L20.925 61.5691ZM15.8315 55.3901C15.6755 55.3901 15.5812 55.4773 15.5376 55.5279C15.4617 55.6193 15.4308 55.7402 15.4533 55.8584C15.9511 58.4782 18.2517 60.3809 20.9236 60.3809C23.5955 60.3809 25.8961 58.4796 26.3939 55.8584C26.4164 55.7402 26.3855 55.6193 26.3095 55.5279C26.2673 55.4759 26.1717 55.3901 26.0156 55.3901H15.8315Z"
              fill="#6F3B66"
            />
            <path
              d="M11.669 42.6612C12.0894 43.0212 12.5099 43.3812 12.9304 43.7412C13.1694 43.9465 13.5196 43.6005 13.2777 43.3938C12.8573 43.0338 12.4368 42.6738 12.0163 42.3138C11.7773 42.1085 11.4271 42.4544 11.669 42.6612Z"
              fill="#6F3B66"
            />
            <path
              d="M13.1215 41.3406C13.3676 41.913 13.6123 42.4853 13.8584 43.0563C13.9836 43.3474 14.4083 43.0956 14.2831 42.8074C14.037 42.235 13.7909 41.6641 13.5462 41.0917C13.4211 40.8006 12.9978 41.0524 13.1215 41.3406Z"
              fill="#6F3B66"
            />
            <path
              d="M15.0791 40.9609C15.0285 41.5726 14.9778 42.1843 14.9272 42.796C14.9019 43.1124 15.3941 43.1096 15.4194 42.796C15.47 42.1843 15.5207 41.5726 15.5699 40.9609C15.5966 40.6445 15.103 40.6473 15.0777 40.9609H15.0791Z"
              fill="#6F3B66"
            />
            <path
              d="M29.5829 42.6612C29.1624 43.0212 28.742 43.3812 28.3215 43.7412C28.0824 43.9465 27.7323 43.6005 27.9742 43.3938C28.3946 43.0338 28.8151 42.6738 29.2356 42.3138C29.4746 42.1085 29.8248 42.4544 29.5829 42.6612Z"
              fill="#6F3B66"
            />
            <path
              d="M28.1303 41.3406C27.8842 41.913 27.6381 42.4853 27.3934 43.0563C27.2682 43.3474 26.845 43.0956 26.9687 42.8074C27.2148 42.235 27.4609 41.6641 27.7056 41.0917C27.8307 40.8006 28.254 41.0524 28.1303 41.3406Z"
              fill="#6F3B66"
            />
            <path
              d="M26.1715 40.9609C26.2221 41.5726 26.2727 42.1843 26.3233 42.796C26.3486 43.1124 25.8565 43.1096 25.8311 42.796C25.7805 42.1843 25.7299 41.5726 25.6793 40.9609C25.6526 40.6445 26.1461 40.6473 26.1715 40.9609Z"
              fill="#6F3B66"
            />
            <path
              d="M16.4688 42.2314C18.7018 42.4826 20.3093 44.4955 20.0582 46.7281L20.056 46.7477V46.7487C19.7956 48.9717 17.7878 50.5693 15.5602 50.319C13.3272 50.0678 11.7196 48.055 11.9707 45.8209M16.4688 42.2314L16.5081 41.8821L16.4688 42.2314ZM16.4688 42.2314C14.2347 41.9803 12.2218 43.5879 11.9707 45.8209M11.9707 45.8209L11.6213 45.7816L11.9707 45.8209Z"
              fill="white"
              stroke="#6F3B66"
              stroke-width="0.703125"
            />
            <path
              d="M19.4705 46.8211C19.2694 48.6154 17.6508 49.9064 15.8578 49.7039C14.0635 49.5028 12.7725 47.8842 12.975 46.0912C13.1761 44.2968 14.7947 43.0059 16.5877 43.2084C18.3806 43.4095 19.673 45.0281 19.4705 46.8211Z"
              fill="#6F3B66"
            />
            <path
              d="M19.1206 45.2233C18.7986 44.6749 18.388 44.2108 17.8058 43.9338C17.5442 43.8086 17.3797 44.2263 17.6384 44.35C18.1053 44.5736 18.4513 44.9646 18.71 45.4047C18.8548 45.6508 19.2669 45.4736 19.1206 45.2233Z"
              fill="white"
            />
            <path
              d="M17.122 43.5867C17.0545 43.5642 16.987 43.5403 16.9195 43.5192C16.8028 43.4812 16.6748 43.5248 16.627 43.6443C16.5848 43.7526 16.6355 43.8989 16.7522 43.9354C16.8197 43.9579 16.8872 43.9818 16.9547 44.0029C17.0714 44.0409 17.1994 43.9973 17.2472 43.8778C17.2894 43.7695 17.2387 43.6233 17.122 43.5867Z"
              fill="white"
            />
            <path
              d="M19.361 46.0333C19.3216 45.918 19.1697 45.873 19.0657 45.9194C18.9462 45.9714 18.9124 46.0994 18.9504 46.2147C18.9504 46.2119 18.9504 46.2105 18.9504 46.2063C18.9433 46.3286 19.0221 46.4397 19.1487 46.4552C19.2654 46.4678 19.3905 46.3792 19.3962 46.2569C19.4004 46.1767 19.3863 46.1092 19.3596 46.0333H19.361Z"
              fill="white"
            />
            <path
              d="M18.9493 46.2133C18.9493 46.2133 18.9493 46.2204 18.9493 46.2246C18.9493 46.2218 18.9493 46.2176 18.9493 46.2148C18.9282 46.1529 18.9437 46.1979 18.9479 46.2148L18.9493 46.2133Z"
              fill="white"
            />
            <path
              d="M26.0313 42.3721C28.2643 42.6232 29.8718 44.6361 29.6207 46.8687L29.6185 46.8883V46.8893C29.3581 49.1123 27.3503 50.71 25.1227 50.4596C22.8897 50.2085 21.2821 48.1956 21.5332 45.9615M26.0313 42.3721L26.0706 42.0227L26.0313 42.3721ZM26.0313 42.3721C23.7972 42.1209 21.7843 43.7285 21.5332 45.9615M21.5332 45.9615L21.1838 45.9222L21.5332 45.9615Z"
              fill="white"
              stroke="#6F3B66"
              stroke-width="0.703125"
            />
            <path
              d="M28.5862 46.8684C28.3851 48.6628 26.7665 49.9537 24.9735 49.7512C23.1792 49.5502 21.8882 47.9316 22.0907 46.1386C22.2918 44.3442 23.9104 43.0533 25.7034 43.2558C27.4964 43.4569 28.7873 45.0741 28.5862 46.8684Z"
              fill="#6F3B66"
            />
            <path
              d="M28.0938 45.4133C27.7717 44.8648 27.3611 44.4008 26.7789 44.1237C26.5174 43.9986 26.3528 44.4162 26.6116 44.54C27.0799 44.7636 27.4244 45.1545 27.6831 45.5947C27.828 45.8408 28.24 45.6622 28.0938 45.4133Z"
              fill="white"
            />
            <path
              d="M26.0972 43.7747C26.0297 43.7522 25.9622 43.7283 25.8947 43.7072C25.778 43.6692 25.65 43.7128 25.6022 43.8323C25.5586 43.9392 25.6106 44.0869 25.7274 44.1234C25.7949 44.1459 25.8624 44.1698 25.9299 44.1909C26.0466 44.2289 26.1746 44.1853 26.2224 44.0658C26.2646 43.9589 26.2139 43.8112 26.0972 43.7747Z"
              fill="white"
            />
            <path
              d="M28.3343 46.2218C28.2949 46.1065 28.1416 46.0615 28.0376 46.1079C27.9194 46.1613 27.8843 46.2879 27.9237 46.4032C27.9237 46.4004 27.9237 46.399 27.9237 46.3947C27.9166 46.5171 27.9954 46.6282 28.1219 46.6423C28.2387 46.6549 28.3638 46.5663 28.3708 46.444C28.3751 46.3638 28.361 46.2963 28.3343 46.2204V46.2218Z"
              fill="white"
            />
            <path
              d="M27.9225 46.4032C27.9225 46.4032 27.9225 46.4102 27.9225 46.4144C27.9225 46.4116 27.9225 46.4074 27.9225 46.4046C27.9014 46.3441 27.9168 46.3877 27.9211 46.4046L27.9225 46.4032Z"
              fill="white"
            />
            <path d="M27.9257 46.4052L27.9238 46.4033L27.9257 46.4052Z" fill="white" />
            <path
              d="M5.58424 30.93C5.54346 30.8808 5.4619 30.1833 5.38737 30.1875C5.31284 30.1931 5.24815 30.885 5.2144 30.93C5.14409 31.0214 4.63643 31.0861 4.64065 31.1747C4.64487 31.2633 5.14409 31.3041 5.2144 31.3913C5.25377 31.4391 5.33675 32.1394 5.41128 32.1338C5.48581 32.1281 5.54909 31.4391 5.58424 31.3913C5.65174 31.3027 6.16362 31.2352 6.1594 31.1452C6.15518 31.0566 5.65174 31.0116 5.58424 30.9286V30.93Z"
              fill="#9694E5"
            />
            <path
              d="M7.97487 38.9456C7.93409 38.8964 7.85253 38.1989 7.77799 38.2031C7.70346 38.2088 7.63877 38.9006 7.60502 38.9456C7.53471 39.0371 7.02706 39.1017 7.03128 39.1903C7.03549 39.2789 7.53471 39.3197 7.60502 39.4069C7.6444 39.4547 7.72737 40.155 7.8019 40.1494C7.87643 40.1438 7.93971 39.4547 7.97487 39.4069C8.04237 39.3183 8.55424 39.2508 8.55002 39.1608C8.54581 39.0722 8.04237 39.0272 7.97487 38.9442V38.9456Z"
              fill="#9694E5"
            />
            <path
              d="M37.6467 45.1331C37.606 45.0839 37.5244 44.3864 37.4499 44.3906C37.3753 44.3963 37.3106 45.0881 37.2769 45.1331C37.2066 45.2246 36.6989 45.2892 36.7032 45.3778C36.7074 45.4664 37.2066 45.5072 37.2769 45.5944C37.3163 45.6422 37.3992 46.3425 37.4738 46.3369C37.5483 46.3313 37.6116 45.6422 37.6467 45.5944C37.7142 45.5058 38.2261 45.4383 38.2219 45.3483C38.2177 45.2597 37.7142 45.2147 37.6467 45.1317V45.1331Z"
              fill="#9694E5"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.71386 36.3903C1.74621 35.9206 1.01917 35.87 0.985423 36.3397C0.953079 36.8094 1.68011 36.86 1.71386 36.3903Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.7599 41.9604C1.7585 40.5555 1.72756 39.1521 1.72615 37.7472C1.72615 37.2874 1.01318 37.2874 1.01318 37.7472C1.01318 39.1521 1.04553 40.5569 1.04693 41.9604C1.04693 42.4202 1.7599 42.4202 1.7599 41.9604Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.78392 43.3738C1.81627 42.9154 1.1047 42.8662 1.07236 43.3246C1.04002 43.783 1.75158 43.8323 1.78392 43.3738Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M20.5207 30.7197C20.1302 30.4568 19.7228 31.0611 20.1127 31.3252C20.5033 31.588 20.9106 30.9837 20.5207 30.7197Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M25.3681 33.4648C24.1507 32.7636 22.9199 32.0887 21.7025 31.3875C21.3043 31.1576 20.9478 31.775 21.346 32.005C22.5627 32.7074 23.7955 33.3818 25.0116 34.0823C25.4098 34.3122 25.7663 33.6948 25.3681 33.4648Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M26.6037 34.1506C26.2229 33.8933 25.8245 34.485 26.2053 34.7422C26.5862 34.9994 26.9846 34.4078 26.6037 34.1506Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M38.0989 40.704C37.7083 40.4412 37.3009 41.0455 37.6908 41.3096C38.0814 41.5724 38.4888 40.9681 38.0989 40.704Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M42.9462 43.4492C41.7289 42.748 40.498 42.0731 39.2807 41.3719C38.8824 41.142 38.5259 41.7594 38.9242 41.9893C40.1408 42.6918 41.3736 43.3662 42.5897 44.0667C42.9879 44.2966 43.3444 43.6791 42.9462 43.4492Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M44.1819 44.1349C43.801 43.8777 43.4026 44.4693 43.7835 44.7266C44.1643 44.9838 44.5627 44.3922 44.1819 44.1349Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M38.1898 62.8222C37.7699 63.0352 38.0993 63.6854 38.5198 63.4735C38.9396 63.2604 38.6103 62.6103 38.1898 62.8222Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M42.9478 59.926C41.7424 60.6476 40.5534 61.3938 39.348 62.1153C38.9532 62.3511 39.3188 62.9632 39.7136 62.7275C40.9197 62.0071 42.1092 61.259 43.3134 60.5381C43.7081 60.3024 43.3426 59.6902 42.9478 59.926Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M44.149 59.1812C43.7389 59.3885 44.0615 60.0246 44.4716 59.8173C44.8818 59.61 44.5592 58.9739 44.149 59.1812Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M15.4085 75.9003C14.9887 76.1134 15.318 76.7635 15.7386 76.5516C16.1584 76.3386 15.8291 75.6884 15.4085 75.9003Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M20.1665 73.0042C18.9611 73.7257 17.7721 74.4719 16.5667 75.1935C16.1719 75.4293 16.5375 76.0414 16.9323 75.8056C18.1384 75.0852 19.3279 74.3371 20.5321 73.6163C20.9269 73.3805 20.5613 72.7684 20.1665 73.0042Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M21.3678 72.2593C20.9576 72.4666 21.2802 73.1027 21.6904 72.8954C22.1005 72.6881 21.7779 72.052 21.3678 72.2593Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.71386 60.9997C1.74621 60.53 1.01917 60.4794 0.985423 60.9491C0.953079 61.4188 1.68011 61.4694 1.71386 60.9997Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.71386 55.2341C1.74621 54.7644 1.01917 54.7138 0.985423 55.1834C0.953079 55.6531 1.68011 55.7038 1.71386 55.2341Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.542 78.1559C12.5743 77.6863 11.8473 77.6356 11.8135 78.1053C11.7812 78.575 12.5082 78.6256 12.542 78.1559Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M28.4326 69.1559C28.465 68.6863 27.7379 68.6356 27.7042 69.1053C27.6718 69.575 28.3989 69.6256 28.4326 69.1559Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.71386 49.4684C1.74621 48.9988 1.01917 48.9481 0.985423 49.4178C0.953079 49.8875 1.68011 49.9381 1.71386 49.4684Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M16.3389 28.8786C16.3712 28.4089 15.6442 28.3583 15.6104 28.828C15.5781 29.2977 16.3051 29.3483 16.3389 28.8786Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.7599 66.5697C1.7585 65.1649 1.72756 63.7614 1.72615 62.3566C1.72615 61.8968 1.01318 61.8968 1.01318 62.3566C1.01318 63.7614 1.04553 65.1663 1.04693 66.5697C1.04693 67.0296 1.7599 67.0296 1.7599 66.5697Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.78392 67.9832C1.81627 67.5248 1.1047 67.4755 1.07236 67.934C1.04002 68.3924 1.75158 68.4416 1.78392 67.9832Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M30.2607 36.7536C30.2931 36.2839 29.566 36.2333 29.5323 36.703C29.5 37.1727 30.227 37.2233 30.2607 36.7536Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.2295 32.6755C14.2618 32.2058 13.5348 32.1552 13.501 32.6248C13.4687 33.0945 14.1957 33.1452 14.2295 32.6755Z"
              fill="#9694E5"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M35.6045 39.5661C35.6368 39.0964 34.9098 39.0458 34.876 39.5155C34.8437 39.9852 35.5707 40.0358 35.6045 39.5661Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M35.3232 43.9255C35.3556 43.4558 34.6285 43.4052 34.5948 43.8748C34.5625 44.3445 35.2895 44.3952 35.3232 43.9255Z"
              fill="#9694E5"
            />
          </g>
          <path
            d="M85.75 48.8359C85.75 49.1328 85.7109 49.4453 85.6328 49.7734C85.5703 50.1016 85.4609 50.4062 85.3047 50.6875C85.1484 50.9688 84.9453 51.2031 84.6953 51.3906C84.4453 51.5781 84.1406 51.6719 83.7812 51.6719C83.2344 51.6719 82.8281 51.5781 82.5625 51.3906C82.2969 51.1875 82.1094 50.9375 82 50.6406C81.8906 50.3438 81.8281 50.0312 81.8125 49.7031C81.8125 49.3594 81.8047 49.0391 81.7891 48.7422C81.7734 48.4453 81.7188 48.2031 81.625 48.0156C81.5469 47.8125 81.3672 47.7109 81.0859 47.7109C80.7734 47.7109 80.5 47.8281 80.2656 48.0625C80.0312 48.2812 79.8281 48.5703 79.6562 48.9297C79.4844 49.2891 79.3359 49.6953 79.2109 50.1484C79.1016 50.6016 79.0078 51.0547 78.9297 51.5078C78.8672 51.9453 78.8203 52.3672 78.7891 52.7734C78.7578 53.1641 78.7422 53.4844 78.7422 53.7344C78.7422 54.0625 78.75 54.4531 78.7656 54.9062C78.7969 55.3594 78.875 55.7969 79 56.2188C79.125 56.625 79.3125 56.9766 79.5625 57.2734C79.8125 57.5547 80.1719 57.6953 80.6406 57.6953C80.9531 57.6953 81.2578 57.6562 81.5547 57.5781C81.8516 57.5 82.1484 57.4141 82.4453 57.3203C82.7422 57.2109 83.0391 57.1172 83.3359 57.0391C83.6484 56.9609 83.9609 56.9219 84.2734 56.9219C84.6328 56.9219 84.9297 57.0703 85.1641 57.3672C85.3984 57.6484 85.5156 57.9688 85.5156 58.3281C85.5156 58.8281 85.3594 59.2969 85.0469 59.7344C84.7344 60.1562 84.3438 60.5312 83.875 60.8594C83.4219 61.1875 82.9219 61.4453 82.375 61.6328C81.8438 61.8203 81.3594 61.9141 80.9219 61.9141C79.7969 61.9141 78.8438 61.6562 78.0625 61.1406C77.2812 60.6406 76.6484 59.9922 76.1641 59.1953C75.6797 58.3828 75.3281 57.4844 75.1094 56.5C74.9062 55.5 74.8047 54.5078 74.8047 53.5234C74.8047 52.8672 74.8672 52.1484 74.9922 51.3672C75.1172 50.5859 75.3047 49.8047 75.5547 49.0234C75.8047 48.2266 76.1172 47.4609 76.4922 46.7266C76.8828 45.9922 77.3359 45.3438 77.8516 44.7812C78.3672 44.2188 78.9453 43.7656 79.5859 43.4219C80.2422 43.0781 80.9609 42.9062 81.7422 42.9062C82.5078 42.9062 83.1484 43.1016 83.6641 43.4922C84.1797 43.8672 84.5938 44.3516 84.9062 44.9453C85.2188 45.5234 85.4375 46.1641 85.5625 46.8672C85.6875 47.5547 85.75 48.2109 85.75 48.8359ZM97.5747 61.5391C96.6216 61.5391 95.7934 61.2969 95.0903 60.8125C94.3872 60.3438 93.8013 59.7109 93.3325 58.9141C92.8794 58.1016 92.5356 57.1641 92.3013 56.1016C92.0825 55.0391 91.9731 53.9219 91.9731 52.75C91.9731 51.6562 92.0903 50.5469 92.3247 49.4219C92.5747 48.2969 92.9497 47.2812 93.4497 46.375C93.9497 45.4688 94.5825 44.7266 95.3481 44.1484C96.1138 43.5703 97.0278 43.2812 98.0903 43.2812C99.1216 43.2812 99.9809 43.5625 100.668 44.125C101.372 44.6719 101.926 45.3906 102.333 46.2812C102.754 47.1562 103.051 48.1406 103.223 49.2344C103.411 50.3281 103.504 51.4219 103.504 52.5156C103.504 53.7344 103.379 54.8906 103.129 55.9844C102.895 57.0625 102.528 58.0156 102.028 58.8438C101.543 59.6719 100.926 60.3281 100.176 60.8125C99.4419 61.2969 98.5747 61.5391 97.5747 61.5391ZM97.8559 48.4609C97.5591 48.4609 97.3013 48.5781 97.0825 48.8125C96.8794 49.0469 96.7075 49.3594 96.5669 49.75C96.4263 50.125 96.3247 50.5547 96.2622 51.0391C96.1997 51.5078 96.1684 51.9844 96.1684 52.4688C96.1684 53.0156 96.1997 53.5625 96.2622 54.1094C96.3403 54.6562 96.4497 55.1484 96.5903 55.5859C96.7309 56.0234 96.9028 56.375 97.1059 56.6406C97.3247 56.9062 97.5825 57.0391 97.8794 57.0391C98.2231 57.0391 98.4966 56.8672 98.6997 56.5234C98.9028 56.1797 99.0591 55.7656 99.1684 55.2812C99.2934 54.7969 99.3716 54.2969 99.4028 53.7812C99.4497 53.2656 99.4731 52.8359 99.4731 52.4922C99.4731 52.0703 99.4419 51.625 99.3794 51.1562C99.3325 50.6875 99.2466 50.2578 99.1216 49.8672C98.9966 49.4609 98.8325 49.125 98.6294 48.8594C98.4263 48.5938 98.1684 48.4609 97.8559 48.4609ZM115.704 61.5391C114.751 61.5391 113.923 61.2969 113.22 60.8125C112.517 60.3438 111.931 59.7109 111.462 58.9141C111.009 58.1016 110.665 57.1641 110.431 56.1016C110.212 55.0391 110.103 53.9219 110.103 52.75C110.103 51.6562 110.22 50.5469 110.454 49.4219C110.704 48.2969 111.079 47.2812 111.579 46.375C112.079 45.4688 112.712 44.7266 113.478 44.1484C114.243 43.5703 115.157 43.2812 116.22 43.2812C117.251 43.2812 118.11 43.5625 118.798 44.125C119.501 44.6719 120.056 45.3906 120.462 46.2812C120.884 47.1562 121.181 48.1406 121.353 49.2344C121.54 50.3281 121.634 51.4219 121.634 52.5156C121.634 53.7344 121.509 54.8906 121.259 55.9844C121.024 57.0625 120.657 58.0156 120.157 58.8438C119.673 59.6719 119.056 60.3281 118.306 60.8125C117.571 61.2969 116.704 61.5391 115.704 61.5391ZM115.985 48.4609C115.688 48.4609 115.431 48.5781 115.212 48.8125C115.009 49.0469 114.837 49.3594 114.696 49.75C114.556 50.125 114.454 50.5547 114.392 51.0391C114.329 51.5078 114.298 51.9844 114.298 52.4688C114.298 53.0156 114.329 53.5625 114.392 54.1094C114.47 54.6562 114.579 55.1484 114.72 55.5859C114.86 56.0234 115.032 56.375 115.235 56.6406C115.454 56.9062 115.712 57.0391 116.009 57.0391C116.353 57.0391 116.626 56.8672 116.829 56.5234C117.032 56.1797 117.188 55.7656 117.298 55.2812C117.423 54.7969 117.501 54.2969 117.532 53.7812C117.579 53.2656 117.603 52.8359 117.603 52.4922C117.603 52.0703 117.571 51.625 117.509 51.1562C117.462 50.6875 117.376 50.2578 117.251 49.8672C117.126 49.4609 116.962 49.125 116.759 48.8594C116.556 48.5938 116.298 48.4609 115.985 48.4609ZM140.232 59.7344C140.232 60.1719 140.123 60.5234 139.904 60.7891C139.685 61.0703 139.349 61.2109 138.896 61.2109C138.537 61.2109 138.201 61.1016 137.888 60.8828C137.591 60.6641 137.31 60.3984 137.044 60.0859C136.794 59.7578 136.568 59.4141 136.365 59.0547C136.177 58.6953 136.021 58.3828 135.896 58.1172C135.568 57.4609 135.24 56.8047 134.912 56.1484C134.599 55.4922 134.287 54.8281 133.974 54.1562L132.568 55.0234C132.583 55.4766 132.56 55.9297 132.498 56.3828C132.435 56.8359 132.388 57.2891 132.357 57.7422C132.326 58.0547 132.271 58.3984 132.193 58.7734C132.13 59.1328 132.021 59.4766 131.865 59.8047C131.708 60.1172 131.498 60.3828 131.232 60.6016C130.966 60.8203 130.638 60.9297 130.248 60.9297C129.888 60.9297 129.591 60.8594 129.357 60.7188C129.123 60.5625 128.927 60.3672 128.771 60.1328C128.63 59.8984 128.529 59.6328 128.466 59.3359C128.404 59.0391 128.373 58.7344 128.373 58.4219V58.2344C128.373 57.3125 128.419 56.3906 128.513 55.4688C128.623 54.5312 128.693 53.5938 128.724 52.6562C128.755 51.4219 128.794 50.1875 128.841 48.9531C128.888 47.7188 128.951 46.4844 129.029 45.25C129.044 44.9688 129.091 44.6953 129.169 44.4297C129.248 44.1641 129.365 43.9297 129.521 43.7266C129.677 43.5078 129.865 43.3359 130.083 43.2109C130.318 43.0859 130.591 43.0234 130.904 43.0234C131.388 43.0234 131.748 43.1953 131.982 43.5391C132.232 43.8828 132.404 44.2969 132.498 44.7812C132.591 45.2656 132.63 45.7656 132.615 46.2812C132.615 46.7969 132.615 47.2266 132.615 47.5703C132.615 48.5547 132.591 49.5391 132.544 50.5234C132.998 50.1484 133.435 49.7188 133.857 49.2344C134.294 48.75 134.677 48.2578 135.005 47.7578C135.208 47.4609 135.443 47.0547 135.708 46.5391C135.974 46.0078 136.263 45.5 136.576 45.0156C136.888 44.5156 137.232 44.0859 137.607 43.7266C137.982 43.3516 138.38 43.1641 138.802 43.1641C139.255 43.1641 139.583 43.2969 139.787 43.5625C140.005 43.8125 140.115 44.1484 140.115 44.5703C140.115 45.1953 140.013 45.8359 139.81 46.4922C139.623 47.1484 139.365 47.7969 139.037 48.4375C138.708 49.0625 138.333 49.6641 137.912 50.2422C137.505 50.8047 137.091 51.2969 136.669 51.7188C137.107 52.4531 137.521 53.1953 137.912 53.9453C138.302 54.6797 138.693 55.4219 139.083 56.1719C139.224 56.4219 139.357 56.6953 139.482 56.9922C139.623 57.2891 139.748 57.5938 139.857 57.9062C139.966 58.2188 140.052 58.5312 140.115 58.8438C140.193 59.1562 140.232 59.4531 140.232 59.7344ZM154.705 44.8516C154.705 45.1484 154.635 45.4141 154.494 45.6484C154.369 45.8828 154.197 46.0859 153.978 46.2578C153.76 46.4141 153.518 46.5469 153.252 46.6562C152.986 46.7656 152.728 46.8438 152.478 46.8906C152.447 48.6562 152.377 50.3594 152.268 52C152.174 53.6406 152.018 55.3359 151.799 57.0859H151.939C152.189 57.0859 152.432 57.1172 152.666 57.1797C152.916 57.2266 153.135 57.3203 153.322 57.4609C153.525 57.5859 153.682 57.75 153.791 57.9531C153.916 58.1406 153.978 58.375 153.978 58.6562C153.978 59.0156 153.861 59.3281 153.627 59.5938C153.408 59.8594 153.111 60.0859 152.736 60.2734C152.377 60.4609 151.971 60.6172 151.518 60.7422C151.064 60.8672 150.611 60.9609 150.158 61.0234C149.705 61.1016 149.275 61.1562 148.869 61.1875C148.463 61.2188 148.135 61.2344 147.885 61.2344C147.697 61.2344 147.463 61.2266 147.182 61.2109C146.916 61.1953 146.658 61.1484 146.408 61.0703C146.174 60.9922 145.971 60.875 145.799 60.7188C145.627 60.5625 145.541 60.3516 145.541 60.0859C145.541 59.7578 145.611 59.4688 145.752 59.2188C145.877 58.9531 146.049 58.7188 146.268 58.5156C146.486 58.3125 146.728 58.1484 146.994 58.0234C147.275 57.8984 147.557 57.8047 147.838 57.7422C147.947 56.8828 148.049 56.0312 148.143 55.1875C148.236 54.3281 148.299 53.4609 148.33 52.5859C148.361 51.6953 148.393 50.8203 148.424 49.9609C148.455 49.0859 148.486 48.2109 148.518 47.3359C148.408 47.3516 148.307 47.3672 148.213 47.3828C148.119 47.3828 148.033 47.3828 147.955 47.3828C147.736 47.3828 147.502 47.3594 147.252 47.3125C147.002 47.2656 146.768 47.1875 146.549 47.0781C146.33 46.9531 146.143 46.8047 145.986 46.6328C145.846 46.4609 145.775 46.2422 145.775 45.9766C145.775 45.4453 145.994 45.0078 146.432 44.6641C146.885 44.3203 147.416 44.0547 148.025 43.8672C148.65 43.6797 149.283 43.5547 149.924 43.4922C150.564 43.4141 151.08 43.375 151.471 43.375C151.689 43.375 151.978 43.3906 152.338 43.4219C152.697 43.4531 153.049 43.5234 153.393 43.6328C153.752 43.7422 154.057 43.8906 154.307 44.0781C154.572 44.2656 154.705 44.5234 154.705 44.8516ZM172.623 50.2188C172.623 51.5938 172.342 52.9219 171.78 54.2031C171.217 55.4688 170.459 56.5938 169.506 57.5781C168.569 58.5625 167.467 59.3516 166.202 59.9453C164.952 60.5234 163.631 60.8125 162.241 60.8125H162.194C161.912 60.8125 161.678 60.6875 161.491 60.4375C161.319 60.1719 161.17 59.8438 161.045 59.4531C160.92 59.0625 160.827 58.625 160.764 58.1406C160.702 57.6562 160.655 57.1875 160.623 56.7344C160.592 56.2656 160.577 55.8516 160.577 55.4922C160.577 55.1172 160.577 54.8359 160.577 54.6484C160.577 54.1172 160.584 53.5938 160.6 53.0781C160.616 52.5469 160.639 52.0156 160.67 51.4844C160.67 51.3125 160.678 51.0625 160.694 50.7344C160.725 50.3906 160.756 50.0078 160.787 49.5859C160.819 49.1641 160.858 48.7266 160.905 48.2734C160.952 47.8047 160.998 47.375 161.045 46.9844C161.108 46.5781 161.162 46.2266 161.209 45.9297C161.272 45.6172 161.342 45.4062 161.42 45.2969C161.623 44.9531 161.936 44.6484 162.358 44.3828C162.78 44.1172 163.233 43.8984 163.717 43.7266C164.217 43.5547 164.717 43.4297 165.217 43.3516C165.733 43.2578 166.178 43.2109 166.553 43.2109C167.584 43.2109 168.475 43.4062 169.225 43.7969C169.991 44.1719 170.623 44.6875 171.123 45.3438C171.639 45.9844 172.014 46.7266 172.248 47.5703C172.498 48.4141 172.623 49.2969 172.623 50.2188ZM168.686 50.8984C168.686 50.5547 168.623 50.2031 168.498 49.8438C168.373 49.4844 168.194 49.1562 167.959 48.8594C167.741 48.5625 167.467 48.3281 167.139 48.1562C166.827 47.9688 166.475 47.875 166.084 47.875C165.756 47.875 165.42 47.9531 165.077 48.1094C164.998 48.1562 164.928 48.3047 164.866 48.5547C164.803 48.7891 164.748 49.0781 164.702 49.4219C164.67 49.7656 164.639 50.1406 164.608 50.5469C164.577 50.9531 164.553 51.3438 164.537 51.7188C164.537 52.0781 164.53 52.3984 164.514 52.6797C164.514 52.9609 164.514 53.1484 164.514 53.2422C164.514 53.3984 164.514 53.6328 164.514 53.9453C164.514 54.2422 164.522 54.5391 164.537 54.8359C164.569 55.1328 164.6 55.3906 164.631 55.6094C164.662 55.8281 164.717 55.9375 164.795 55.9375C165.264 55.9375 165.725 55.7578 166.178 55.3984C166.647 55.0234 167.069 54.5703 167.444 54.0391C167.819 53.5078 168.116 52.9531 168.334 52.375C168.569 51.7969 168.686 51.3047 168.686 50.8984ZM186.862 44.3125C186.862 44.6719 186.745 44.9844 186.511 45.25C186.292 45.5156 186.026 45.7344 185.714 45.9062C185.433 46.0781 185.143 46.2656 184.847 46.4688C184.55 46.6562 184.276 46.875 184.026 47.125C183.776 47.3594 183.573 47.625 183.417 47.9219C183.261 48.2188 183.183 48.5547 183.183 48.9297C183.183 49.2578 183.261 49.5547 183.417 49.8203C183.573 50.0859 183.761 50.3516 183.979 50.6172C184.214 50.8672 184.464 51.1328 184.729 51.4141C185.011 51.6797 185.261 51.9844 185.479 52.3281C185.714 52.6562 185.909 53.0312 186.065 53.4531C186.222 53.875 186.3 54.3672 186.3 54.9297C186.3 55.3203 186.214 55.7656 186.042 56.2656C185.886 56.75 185.667 57.2422 185.386 57.7422C185.104 58.2422 184.768 58.7266 184.378 59.1953C184.003 59.6641 183.597 60.0859 183.159 60.4609C182.737 60.8203 182.292 61.1094 181.823 61.3281C181.354 61.5625 180.901 61.6797 180.464 61.6797C179.979 61.6797 179.55 61.5469 179.175 61.2812C178.8 61.0156 178.612 60.6172 178.612 60.0859C178.612 59.7266 178.698 59.3984 178.87 59.1016C179.042 58.7891 179.253 58.4922 179.503 58.2109C179.753 57.9297 180.026 57.6562 180.323 57.3906C180.636 57.125 180.917 56.8594 181.167 56.5938C181.417 56.3281 181.628 56.0547 181.8 55.7734C181.972 55.4766 182.058 55.1641 182.058 54.8359C182.058 54.3828 181.893 53.9219 181.565 53.4531C181.253 52.9688 180.901 52.4766 180.511 51.9766C180.12 51.4609 179.761 50.9375 179.433 50.4062C179.12 49.875 178.964 49.3359 178.964 48.7891C178.964 48.1172 179.143 47.4141 179.503 46.6797C179.862 45.9297 180.323 45.25 180.886 44.6406C181.448 44.0156 182.081 43.5 182.784 43.0938C183.487 42.6875 184.183 42.4844 184.87 42.4844C185.417 42.4844 185.886 42.6484 186.276 42.9766C186.667 43.3047 186.862 43.75 186.862 44.3125Z"
            fill="#14142A"
          />
          <defs>
            <clipPath id="clip0_9447_8574">
              <rect width="51.1875" height="56.5312" fill="white" transform="translate(0 24)" />
            </clipPath>
          </defs>
        </svg>
      </Box>
    );

    if (disabledLink) {
      return logo;
    }

    return (
      <Link component={RouterLink} href="/" sx={{ display: 'contents' }}>
        {logo}
      </Link>
    );
  }
);

export default Logo;
